import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { useTheme } from '../context/ThemeContext';
import * as pickerService from '../services/api/picker';
import { ArrowLeftIcon, CameraIcon, PlusIcon, MinusIcon, XIcon, CheckIcon, QuestionIcon } from '../components/Icons';
import './LogItem.css';

const LogItem = () => {
  const navigate = useNavigate();
  const { orderId, itemId } = useParams();
  const { pickupItems, addPickupItem, updatePickupItem } = useApp();
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    weight: '',
    productId: '',
    category: '',
    rate: '',
    photo: null,
    photoFile: null,
    imageUrl: null
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [products, setProducts] = useState([]);
  const [productPrices, setProductPrices] = useState({});

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      setIsLoading(true);
      try {
        const result = await pickerService.getProducts();
        if (result.success && result.data && Array.isArray(result.data)) {
          // Filter only products with status ACTIVE
          const activeProducts = result.data.filter(product => 
            product.status === 'ACTIVE'
          );
          
          setProducts(activeProducts);
          // Create price map
          const prices = {};
          activeProducts.forEach(product => {
            if (product.product_id) {
              prices[product.product_id] = parseFloat(product.price || 0);
            }
          });
          setProductPrices(prices);
          
          // Set default product if available
          if (activeProducts.length > 0 && !formData.productId) {
            const firstProduct = activeProducts[0];
            setFormData(prev => ({
              ...prev,
              productId: firstProduct.product_id?.toString() || '',
              category: firstProduct.product_name || ''
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        setErrors({ ...errors, general: 'Failed to load products. Please try again.' });
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    const existingItem = pickupItems.find(item => item.id === itemId);
    if (existingItem) {
      setFormData({
        weight: existingItem.weight ? existingItem.weight.toString() : '',
        productId: existingItem.productId || '',
        category: existingItem.category || '',
        rate: existingItem.rate ? existingItem.rate.toString() : '',
        photo: existingItem.photo || null,
        photoFile: null,
        imageUrl: existingItem.imageUrl || null
      });
    }
  }, [itemId, pickupItems]);

  const handleChange = (e) => {
    const value = e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
    // Clear error when user types
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: '' });
    }
  };

  const handleWeightIncrement = () => {
    const currentWeight = parseFloat(formData.weight) || 0;
    const newWeight = (currentWeight + 0.5).toFixed(2);
    setFormData({ ...formData, weight: newWeight });
    if (errors.weight) {
      setErrors({ ...errors, weight: '' });
    }
  };

  const handleWeightDecrement = () => {
    const currentWeight = parseFloat(formData.weight) || 0;
    const newWeight = Math.max(0, currentWeight - 0.5).toFixed(2);
    setFormData({ ...formData, weight: newWeight });
    if (errors.weight) {
      setErrors({ ...errors, weight: '' });
    }
  };

  const handleRateChange = (e) => {
    const value = e.target.value;
    setFormData({
      ...formData,
      rate: value
    });
    // Clear error when user types
    if (errors.rate) {
      setErrors({ ...errors, rate: '' });
    }
  };

  const handleRateIncrement = () => {
    const currentRate = parseFloat(formData.rate) || 0;
    const newRate = (currentRate + 0.5).toFixed(2);
    setFormData({ ...formData, rate: newRate });
  };

  const handleRateDecrement = () => {
    const currentRate = parseFloat(formData.rate) || 0;
    const newRate = Math.max(0, (currentRate - 0.5).toFixed(2));
    setFormData({ ...formData, rate: newRate });
  };

  const handleCategorySelect = (productId, category) => {
    setFormData({
      ...formData,
      productId: productId.toString(),
      category: category
    });
    if (errors.category) {
      setErrors({ ...errors, category: '' });
    }
  };

  const handlePhotoCapture = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors({ ...errors, photo: 'Image size should be less than 5MB' });
        return;
      }
      
      // Show preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          photo: reader.result,
          photoFile: file
        });
        if (errors.photo) {
          setErrors({ ...errors, photo: '' });
        }
      };
      reader.readAsDataURL(file);

      // Upload image to API
      setIsUploading(true);
      try {
        const uploadResult = await pickerService.uploadOrderImage(orderId, file);
        if (uploadResult.success && uploadResult.imageUrl) {
          setFormData(prev => ({
            ...prev,
            imageUrl: uploadResult.imageUrl
          }));
        } else {
          setErrors({ ...errors, photo: uploadResult.message || 'Failed to upload image' });
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        setErrors({ ...errors, photo: 'Failed to upload image. Please try again.' });
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleRemovePhoto = (e) => {
    e.stopPropagation();
    setFormData({
      ...formData,
      photo: null,
      photoFile: null,
      imageUrl: null
    });
    // Reset file input
    const fileInput = document.getElementById('photo-input');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.weight || parseFloat(formData.weight) <= 0) {
      newErrors.weight = 'Please enter a valid weight';
    }
    
    if (!formData.productId) {
      newErrors.category = 'Please select a plastic type';
    }

    // Photo is optional, no validation needed

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    // Upload image if provided (optional)
    let imageUrl = formData.imageUrl;
    if (!imageUrl && formData.photoFile) {
      setIsUploading(true);
      try {
        const uploadResult = await pickerService.uploadOrderImage(orderId, formData.photoFile);
        if (uploadResult.success && uploadResult.imageUrl) {
          imageUrl = uploadResult.imageUrl;
        } else {
          // Photo is optional, so just log the error but continue
          console.warn('Failed to upload image:', uploadResult.message);
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        // Photo is optional, so just log the error but continue
      } finally {
        setIsUploading(false);
      }
    }

    // Calculate actual price using negotiated rate if provided, otherwise use base price
    const actualQuantity = parseFloat(formData.weight);
    const negotiatedRate = formData.rate ? parseFloat(formData.rate) : null;
    const basePrice = productPrices[formData.productId] || 0;
    const rateToUse = negotiatedRate != null && !isNaN(negotiatedRate) && negotiatedRate > 0 
      ? negotiatedRate 
      : basePrice;
    const actualPrice = Math.round(actualQuantity * rateToUse);

    // Prepare product data for API
    // Use same values for quantity/estimate_price as actual_quantity/actual_price
    const productData = {
      product_id: formData.productId.toString(),
      quantity: actualQuantity, // Same as actual_quantity
      estimate_price: actualPrice, // Same as actual_price
      image: imageUrl || '', // Optional, can be empty string
      actual_quantity: actualQuantity,
      actual_price: actualPrice,
      customer_rate: negotiatedRate != null && !isNaN(negotiatedRate) && negotiatedRate > 0 
        ? negotiatedRate 
        : null
    };

    setIsLoading(true);
    try {
      const result = await pickerService.addProductsToOrder(orderId, [productData]);
      
      if (result.success) {
        // Also update local state for UI consistency
    const itemData = {
      id: itemId,
          weight: actualQuantity,
          productId: formData.productId,
      category: formData.category,
          rate: negotiatedRate || basePrice,
          photo: formData.photo,
          imageUrl: imageUrl
    };

    const existingItem = pickupItems.find(item => item.id === itemId);
    if (existingItem) {
      updatePickupItem(itemId, itemData);
    } else {
      addPickupItem(itemData);
    }

    navigate(`/pickup/${orderId}/process`);
      } else {
        setErrors({ ...errors, general: result.message || 'Failed to add product. Please try again.' });
      }
    } catch (error) {
      console.error('Error adding product:', error);
      setErrors({ ...errors, general: error.message || 'Failed to add product. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="log-item-screen">
      <div className="log-item-container">
        <div className="header">
          <button className="header-back-btn" onClick={() => navigate(`/pickup/${orderId}/process`)}>
            <ArrowLeftIcon size={24} color={theme === 'dark' ? '#ffffff' : '#1a1a1a'} />
          </button>
          <div className="header-title-wrapper">
            <h1 className="header-title">Additional Item</h1>
            <div className="header-order-id">Order #{orderId}</div>
          </div>
          <button className="header-cancel-btn" onClick={() => navigate(`/pickup/${orderId}/process`)}>
            Cancel
          </button>
        </div>


        <form onSubmit={handleSubmit} className="log-item-form">
          <div className="form-group">
            <label htmlFor="weight" className="form-label">
              Net Weight (kg)
              <span className="label-hint">Enter the actual weight of the item</span>
            </label>
            <div className={`weight-input-wrapper ${errors.weight ? 'error' : ''} ${formData.weight ? 'filled' : ''}`}>
              <button
                type="button"
                className="weight-btn decrement"
                onClick={handleWeightDecrement}
                disabled={!formData.weight || parseFloat(formData.weight) <= 0}
              >
                <MinusIcon size={18} color={theme === 'dark' ? '#ffffff' : '#1a1a1a'} />
              </button>
              <div className="weight-input-inner">
              <span className="weight-icon">⚖</span>
              <input
                type="number"
                id="weight"
                name="weight"
                className="weight-input"
                step="0.01"
                min="0"
                  placeholder="0.00"
                value={formData.weight}
                onChange={handleChange}
              />
              <span className="weight-unit">kg</span>
              </div>
              <button
                type="button"
                className="weight-btn increment"
                onClick={handleWeightIncrement}
              >
                <PlusIcon size={18} color={theme === 'dark' ? '#ffffff' : '#1a1a1a'} />
              </button>
            </div>
            {errors.weight && <span className="error-message">{errors.weight}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="rate" className="form-label">
              Negotiated Rate (₹/KG)
              <span className="label-hint">Enter the negotiated rate for this item</span>
            </label>
            <div className={`weight-input-wrapper ${errors.rate ? 'error' : ''} ${formData.rate ? 'filled' : ''}`}>
              <button
                type="button"
                className="weight-btn decrement"
                onClick={handleRateDecrement}
                disabled={!formData.rate || parseFloat(formData.rate) <= 0}
              >
                <MinusIcon size={18} color={theme === 'dark' ? '#ffffff' : '#1a1a1a'} />
              </button>
              <div className="weight-input-inner">
                <span className="weight-icon">₹</span>
                <input
                  type="number"
                  id="rate"
                  name="rate"
                  className="weight-input"
                  step="0.5"
                  min="0"
                  placeholder="0.0"
                  value={formData.rate}
                  onChange={handleRateChange}
                />
                <span className="weight-unit">/kg</span>
              </div>
              <button
                type="button"
                className="weight-btn increment"
                onClick={handleRateIncrement}
              >
                <PlusIcon size={18} color={theme === 'dark' ? '#ffffff' : '#1a1a1a'} />
              </button>
            </div>
            {errors.rate && <span className="error-message">{errors.rate}</span>}
            {formData.productId && productPrices[formData.productId] && (
              <div style={{ marginTop: '8px', fontSize: '14px', color: theme === 'dark' ? '#94a3b8' : '#6b7280' }}>
                Base Price: ₹{productPrices[formData.productId].toFixed(2)}/kg
              </div>
            )}
          </div>

          <div className="form-group">
            <div className="label-with-link">
              <label htmlFor="category" className="form-label">
                Plastic Type
                <span className="label-hint">Select the type of plastic material</span>
              </label>
              <a href="#guide" className="guide-link" onClick={(e) => { e.preventDefault(); }}>
                <QuestionIcon size={16} color={theme === 'dark' ? '#94a3b8' : '#6b7280'} />
                Guide
              </a>
            </div>
            <div className={`plastic-type-grid ${errors.category ? 'error' : ''}`} tabIndex={0}>
              {isLoading ? (
                <div className="loading-products">Loading products...</div>
              ) : products.length > 0 ? (
                products.map((product) => {
                  const productId = product.product_id?.toString() || '';
                  const isSelected = formData.productId === productId;
                  const price = productPrices[productId] || 0;
                  
                  return (
                <button
                      key={productId}
                  type="button"
                      className={`plastic-type-btn ${isSelected ? 'selected' : ''}`}
                      onClick={() => handleCategorySelect(productId, product.product_name || product.name || '')}
                    >
                      {isSelected && (
                        <div className="selected-indicator">
                          <CheckIcon size={14} color="#ffffff" />
                        </div>
                      )}
                      <span className="type-icon">♻</span>
                      <span className="type-label">{product.product_name || product.name || 'Product'}</span>
                      {price > 0 && (
                        <span className="type-sublabel">₹{price.toFixed(2)}/kg</span>
                      )}
                </button>
                  );
                })
              ) : (
                <div className="no-products">No products available</div>
              )}
            </div>
            {errors.category && <span className="error-message">{errors.category}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="photo" className="form-label">
              Photo Proof
              <span className="label-hint">Capture a photo of the item as evidence</span>
            </label>
            <label htmlFor="photo-input" className={`photo-upload-area ${formData.photo ? 'has-photo' : ''} ${errors.photo ? 'error' : ''} ${isUploading ? 'uploading' : ''}`}>
              {formData.photo ? (
                <div className="photo-preview-wrapper">
                <img src={formData.photo} alt="Item" className="photo-preview" />
                  {!isUploading && (
                    <button
                      type="button"
                      className="remove-photo-btn"
                      onClick={handleRemovePhoto}
                      aria-label="Remove photo"
                    >
                      <XIcon size={18} color="#ffffff" />
                    </button>
                  )}
                  {isUploading && (
                    <div className="upload-overlay">
                      <div className="spinner-small"></div>
                      <span>Uploading...</span>
                    </div>
                  )}
                  <div className="photo-overlay">
                    <CameraIcon size={20} color="#ffffff" />
                    <span>Tap to change</span>
                  </div>
                </div>
              ) : (
                <div className="photo-upload-placeholder">
                  <div className="camera-icon-wrapper">
                    <CameraIcon size={40} color={theme === 'dark' ? '#94a3b8' : '#6b7280'} />
                  </div>
                  <span className="photo-text">Tap to capture evidence</span>
                  <span className="photo-hint">Max 5MB • JPG, PNG</span>
                </div>
              )}
              <input
                type="file"
                id="photo-input"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoCapture}
                disabled={isUploading}
                style={{ display: 'none' }}
              />
            </label>
            {errors.photo && <span className="error-message">{errors.photo}</span>}
          </div>

          {errors.general && (
            <div className="error-message general-error">
              {errors.general}
            </div>
          )}

          <button 
            type="submit" 
            className="btn-primary confirm-btn" 
            disabled={isLoading || isUploading}
          >
            {isLoading || isUploading ? (
              <>
                <div className="spinner-small"></div>
                <span>{isUploading ? 'Uploading...' : 'Adding Item...'}</span>
              </>
            ) : (
              <>
                <CheckIcon size={20} color="#ffffff" />
                <span>Confirm & Add Item</span>
              </>
            )}
          </button>
          
          <div className="form-summary">
            {formData.weight && (
              <div className="summary-item">
                <span className="summary-label">Weight:</span>
                <span className="summary-value">{parseFloat(formData.weight).toFixed(2)} kg</span>
              </div>
            )}
            {formData.category && (
              <div className="summary-item">
                <span className="summary-label">Type:</span>
                <span className="summary-value">{formData.category}</span>
              </div>
            )}
            {formData.weight && (
              <div className="summary-item">
                <span className="summary-label">Price:</span>
                <span className="summary-value">
                  {(() => {
                    const weight = parseFloat(formData.weight) || 0;
                    const rate = formData.rate ? parseFloat(formData.rate) : (productPrices[formData.productId] || 0);
                    return `₹${(weight * rate).toFixed(2)}`;
                  })()}
                </span>
              </div>
            )}
            {formData.photo && (
              <div className="summary-item">
                <span className="summary-label">Photo:</span>
                <span className="summary-value">✓ {isUploading ? 'Uploading...' : 'Captured'}</span>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default LogItem;

